import { useEffect } from 'react';
import { decidePostStripeReturnAction, decideRefundReturnAction } from './CreditCardSetup.utils';
import type { PayoutAccountInfo } from './CreditCardSetUp.context';

type CookedLineItem = Readonly<{
  price: string;
  currency: string;
}>;

type RefundReturnOrder<TLineItem extends CookedLineItem = CookedLineItem> = Readonly<{
  id: string;
  lineItems: ReadonlyArray<TLineItem>;
}>;

type KycForSgfcCompletedPayload<TLineItem extends CookedLineItem> = {
  orderId: string;
  registryItem: TLineItem;
  amount: number;
};

type PostStripeReturnEffectParams = Readonly<{
  checkStripeStatus: string | null;
  fetchingPayoutInfo: boolean;
  payoutAccountInfo: PayoutAccountInfo | null;
  preloadedOrderIdForRefund: string | null;
  setSetupCreditCard: (next: string | null) => void;
  setCheckStripeStatus: (next: string | null) => void;
}>;

/**
 * Drives the post-Stripe return flow inside CreditCardSetupProvider.
 * Opens the setup dialog on non-refund success, leaves it closed on refund success
 * or back-out, and clears the check_stripe_status marker once a terminal decision is made.
 */
export const usePostStripeReturnEffect = ({
  checkStripeStatus,
  fetchingPayoutInfo,
  payoutAccountInfo,
  preloadedOrderIdForRefund,
  setSetupCreditCard,
  setCheckStripeStatus,
}: PostStripeReturnEffectParams): void => {
  useEffect(() => {
    const action = decidePostStripeReturnAction({
      checkStripeStatus,
      fetchingPayoutInfo,
      payoutStatus: payoutAccountInfo?.status,
      preloadedOrderIdForRefund,
    });
    if (action === 'wait') return;
    if (action === 'openSetupDialog') setSetupCreditCard('true');
    if (checkStripeStatus === 'true') setCheckStripeStatus(null);
  }, [checkStripeStatus, fetchingPayoutInfo, payoutAccountInfo, preloadedOrderIdForRefund, setSetupCreditCard, setCheckStripeStatus]);
};

type RefundReturnEffectParams<TLineItem extends CookedLineItem, TOrder extends RefundReturnOrder<TLineItem>> = Readonly<{
  preloadedOrderIdForRefund: string | null;
  fetchingPayoutInfo: boolean;
  payoutAccountInfo: PayoutAccountInfo | null;
  orders: ReadonlyArray<TOrder> | undefined;
  openRefundHandleDialog: (args: { orderId: string }) => void;
  setPreloadedOrderIdForRefund: (next: string | null) => void;
  kycForSgfcCompleted: (payload: KycForSgfcCompletedPayload<TLineItem>) => unknown;
}>;

/**
 * Drives the post-Stripe return flow inside GiftTrackerProvider for the refund path.
 * Opens the refund dialog and fires telemetry on success, clears the URL marker on back-out.
 */
export const useRefundReturnEffect = <TLineItem extends CookedLineItem, TOrder extends RefundReturnOrder<TLineItem>>({
  preloadedOrderIdForRefund,
  fetchingPayoutInfo,
  payoutAccountInfo,
  orders,
  openRefundHandleDialog,
  setPreloadedOrderIdForRefund,
  kycForSgfcCompleted,
}: RefundReturnEffectParams<TLineItem, TOrder>): void => {
  useEffect(() => {
    if (!preloadedOrderIdForRefund) return;
    const action = decideRefundReturnAction({
      preloadedOrderIdForRefund,
      fetchingPayoutInfo,
      payoutStatus: payoutAccountInfo?.status,
    });
    if (action === 'wait' || action === 'noop') return;

    if (action === 'clearAndSkip') {
      setPreloadedOrderIdForRefund(null);
      return;
    }

    const orderToRefund = orders?.find(order => order.id === preloadedOrderIdForRefund);
    if (orderToRefund) {
      openRefundHandleDialog({ orderId: preloadedOrderIdForRefund });
      setPreloadedOrderIdForRefund(null);

      const lineItem = orderToRefund.lineItems[0];
      kycForSgfcCompleted({
        orderId: orderToRefund.id,
        registryItem: lineItem,
        amount: Number.parseInt(lineItem.price.replace(lineItem.currency, '')),
      });
    }
  }, [fetchingPayoutInfo, kycForSgfcCompleted, openRefundHandleDialog, orders, payoutAccountInfo, preloadedOrderIdForRefund, setPreloadedOrderIdForRefund]);
};
